/**
 * Display-formatting helpers for the enrollment preview.
 */

/** Words kept lowercase in title case unless they're the first word. */
const MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
  "via",
]);

/**
 * Title-case a free-text string (company / location) for display.
 *
 * - Capitalizes the first letter of each significant word.
 * - Leaves an already mixed-case token alone (so brand casing like
 *   "iOS", "GitHub", "LinkedIn" survives) — we only touch tokens that
 *   are all-lower or all-upper.
 * - Keeps minor words ("of", "the", …) lowercase unless first.
 */
export function titleCase(input: string | null | undefined): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/(\s+|,)/)
    .map((token) => {
      if (/^\s+$/.test(token) || token === ",") return token;
      return formatWord(token);
    })
    .join("");
}

function formatWord(word: string): string {
  // Only normalise clearly un-cased (all-lowercase) tokens. Acronyms
  // ("NVIDIA", "IBM"), brand casing ("iOS", "GitHub"), and already
  // title-cased words are left exactly as the data has them.
  const isAllLower = word === word.toLowerCase();
  if (!isAllLower) return word;

  if (MINOR_WORDS.has(word)) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}
